import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useOnlineStatus } from './useOnlineStatus'
import { createOfflineCheckin, cacheCheckins } from '../db/mobileDbOperations'

export function useMobileCheckin(userProfileId, companyId) {
  const isOnline = useOnlineStatus()
  const [currentStatus, setCurrentStatus] = useState(null)
  const [recentHistory, setRecentHistory] = useState([])
  const [siteLocations, setSiteLocations] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchAll = useCallback(async () => {
    if (!userProfileId || !companyId) return
    setLoading(true)

    if (isOnline) {
      try {
        const { data, error } = await supabase.rpc('app_get_checkin_data_v2', {
          p_user_id: userProfileId,
          p_company_id: companyId,
        })

        if (error) throw error

        setCurrentStatus(data?.current_status || null)
        setRecentHistory(data?.recent_history || [])
        setSiteLocations(data?.site_locations || [])

        // Cache history
        const history = data?.recent_history || []
        await cacheCheckins(history.map(c => ({ ...c, syncStatus: 'synced' })))
      } catch (err) {
        console.error('[Checkin] Error:', err)
      }
    }

    setLoading(false)
  }, [userProfileId, companyId, isOnline])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const isClockedIn = currentStatus && currentStatus.check_in_time && !currentStatus.check_out_time

  const clockIn = async (siteLocation, coords) => {
    if (!siteLocation) throw new Error('Please select a site')

    if (isOnline) {
      const { data, error } = await supabase.rpc('app_clock_in_v2', {
        p_user_id: userProfileId,
        p_site_location_id: siteLocation.id,
        p_latitude: coords?.latitude || null,
        p_longitude: coords?.longitude || null,
      })

      if (error) throw error
      setCurrentStatus(data)
      await fetchAll()
      return data
    } else {
      const now = new Date().toISOString()
      await createOfflineCheckin({
        timestamp: now,
        action: 'clock_in',
        siteLocationId: siteLocation.id,
        projectId: siteLocation.project_id,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
      })
      setCurrentStatus({
        check_in_time: now,
        check_out_time: null,
        site_location: { name: siteLocation.name },
      })
      return { offline: true }
    }
  }

  const clockOut = async () => {
    if (!currentStatus?.id) return

    if (isOnline) {
      const { data, error } = await supabase.rpc('app_clock_out_v2', {
        p_checkin_id: currentStatus.id,
      })

      if (error) throw error
      setCurrentStatus(data)
      await fetchAll()
      return data
    } else {
      const now = new Date().toISOString()
      await createOfflineCheckin({
        timestamp: now,
        action: 'clock_out',
        checkinId: currentStatus.id,
      })
      setCurrentStatus(prev => ({ ...prev, check_out_time: now }))
      return { offline: true }
    }
  }

  return {
    currentStatus,
    isClockedIn,
    recentHistory,
    siteLocations,
    loading,
    clockIn,
    clockOut,
    refetch: fetchAll,
  }
}
